/**
 * Qué se tira a la basura cuando cambias de negocio.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL FALLO QUE HACE FALTA ENTENDER PARA NO VOLVER A ROMPERLO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El backend está aislado a conciencia: cada consulta filtra por `tenant_id`, y
 * por debajo hay Row Level Security en Postgres por si alguna se escapa. Ningún
 * negocio puede leer los datos de otro. Eso funciona.
 *
 * **El navegador no tenía nada de eso.** Al cambiar de negocio, la sesión nueva
 * llegaba correcta pero el resto del estado del cliente se quedaba donde
 * estaba:
 *
 *   · El carrito seguía con el corte de cabello de la barbería, en la bodega.
 *   · React Query servía de su caché los ajustes, las monedas y el catálogo del
 *     negocio anterior, porque sus claves —`["negocio"]`, `["monedas-negocio"]`—
 *     no llevan el negocio dentro.
 *   · El catálogo guardado para trabajar sin internet mezclaba los dos.
 *
 * Visto desde fuera parece una fuga de datos entre negocios, y en la práctica lo
 * es aunque el servidor esté bien: al dueño le da igual dónde estuvo el fallo,
 * lo que ve es la mercancía de un local dentro del otro.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE BORRA TODO EN VEZ DE PONERLE EL NEGOCIO A CADA CLAVE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La alternativa era meter el `tenant_id` en cada `queryKey` y en cada llave de
 * localStorage. Es más elegante sobre el papel y peor en la práctica: son
 * decenas de sitios, y **basta con olvidar uno para que vuelva la fuga** — sin
 * que nada avise, porque solo se nota si alguien tiene dos negocios y se fija.
 *
 * Borrar todo de golpe es una línea, no se puede olvidar a medias, y el coste es
 * una recarga de datos que ocurre una vez cada vez que alguien conmuta de
 * local: dos veces al día como mucho.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE **NO** SE BORRA, Y ES A PROPÓSITO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · **Las ventas pendientes de enviar.** Son dinero ya cobrado que todavía no
 *     llegó al servidor. Cada una lleva dentro a qué negocio va, así que se
 *     mandan bien aunque ahora estés mirando otro. Borrarlas sería borrar
 *     ventas reales.
 *   · **La impresora.** Es de este mostrador, no de este negocio. Si el mismo
 *     computador atiende dos locales, la impresora sigue siendo la misma.
 *   · **El tema claro/oscuro.** Es de la persona.
 */

import type { QueryClient } from "@tanstack/react-query";

/**
 * El `QueryClient` de la aplicación, para poder vaciarlo desde fuera de React.
 *
 * El store de sesión es un Zustand normal y no tiene hooks disponibles, así que
 * `Providers` lo registra aquí al arrancar. Es el mismo patrón que ya usa
 * `registrarCierreDeSesion` en `api.ts`.
 */
let cliente: QueryClient | null = null;

export function registrarQueryClient(qc: QueryClient) {
  cliente = qc;
}

/**
 * Vuelve a pedir las consultas (todas, o las que empiezan por `clave`).
 *
 * `invalidateQueries()` y no `clear()`: aquí el negocio es el MISMO, así que
 * lo que se ve sigue siendo correcto mientras llega lo nuevo. Lo usan la
 * sesión cuando cambian los permisos (`actualizarAcceso`) y los clientes del
 * aparato al ponerse al día.
 */
export function invalidarConsultas(clave?: readonly unknown[]): void {
  void cliente?.invalidateQueries(clave ? { queryKey: clave } : undefined);
}

/**
 * Deja el navegador como si acabaras de entrar a este negocio.
 *
 * Se llama desde `aplicarSesion` cuando detecta que el negocio cambió, así que
 * cubre TODOS los caminos: el conmutador del nombre, crear un negocio nuevo,
 * aceptar una invitación y el selector de después de iniciar sesión.
 */
export async function limpiarEstadoDelNegocio(): Promise<void> {
  // 1. La caché de consultas. `clear()` y no `invalidateQueries()`: invalidar
  //    marca los datos como viejos pero LOS SIGUE ENSEÑANDO mientras recarga,
  //    y esos datos son del otro negocio. Vaciar deja las pantallas cargando,
  //    que es lo correcto — todavía no se sabe nada de este negocio.
  cliente?.clear();

  // 2. El carrito. Se importa aquí dentro y no arriba para no crear un ciclo:
  //    el store del carrito acaba importando el de sesión.
  const { useCarrito } = await import("@/stores/carrito");
  useCarrito.getState().vaciar();

  // 3. El catálogo sin conexión.
  const { limpiarCacheDeDatos } = await import("@/lib/db");
  await limpiarCacheDeDatos();

  // 4. Y la foto de lo último que se vio en cada pantalla. Es exactamente el
  //    mismo motivo del punto 1: esa foto es del otro negocio.
  const { olvidarLasConsultas } = await import("@/lib/memoriaDeConsultas");
  await olvidarLasConsultas();
}
